import { useCallback, useRef } from 'react';
import { useConfirm } from '../ConfirmDialog';
import { giveRecordingReason, needsRecordingReason } from '../../lib/recordings';

/**
 * WHY ARE YOU OPENING THIS? (Gordon, 3 November). Somebody who was not on a
 * call - an administrator looking in - says why before its transcript, its
 * audio, a summary, to-dos or minutes are made or opened. Once per recording
 * per person per day: the server keeps the reason (routes/recordings.js,
 * recording_access.reason) and lets everything through until midnight in
 * Malta. The people on the call, and whoever recorded it, are never asked -
 * the server never answers them 428, so this never shows for them.
 *
 * The wording follows the chat review's question (pages/ChatArchive.js):
 * what is recorded and who can read it, said before the box.
 *
 *   const [withReason, reasonDialog] = useRecordingReason();
 *   const r = await withReason(id, () => getRecording(id), 'transcript');
 *   ...
 *   return (<>…{reasonDialog}</>);
 *
 * withReason runs the call; on 428 reason_required it asks, records the
 * reason (POST /recordings/:id/reason) and runs the call again. Cancelled,
 * it throws the server's answer, whose sentence says a reason is needed.
 * Two calls asking at once (the transcript and the audio) share one
 * question.
 */
export default function useRecordingReason() {
  const [confirm, dialog] = useConfirm();
  const asking = useRef(new Map());   // recording id -> Promise<boolean>

  const ask = useCallback((id, about, what) => {
    if (asking.current.has(id)) return asking.current.get(id);
    const p = (async () => {
      let short = false;
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const typed = await confirm({
          title: about ? `Why are you opening “${about}”?` : 'Why are you opening this recording?',
          message: 'You were not on this call. Your name, this recording, your reason and the time are recorded in the Audit log. '
            + `You will not be asked again for this recording today.${short ? ' Say a little more - at least 3 characters.' : ''}`,
          confirmLabel: 'Open it',
          prompt: { label: 'Reason', placeholder: 'For example: complaint HR-12 about this call', required: true, multiline: true }
        });
        if (typed === false) return false;
        const why = String(typed).trim();
        if (why.length < 3) { short = true; continue; }
        try {
          // eslint-disable-next-line no-await-in-loop
          await giveRecordingReason(id, why.slice(0, 500), what);
          return true;
        } catch {
          return false;
        }
      }
    })().finally(() => asking.current.delete(id));
    asking.current.set(id, p);
    return p;
  }, [confirm]);

  const withReason = useCallback(async (id, call, what = 'transcript') => {
    try {
      return await call();
    } catch (err) {
      if (!needsRecordingReason(err)) throw err;
      const given = await ask(id, err.response?.data?.recording?.about || null, what);
      if (!given) throw err;
      return call();
    }
  }, [ask]);

  return [withReason, dialog];
}
